import { useCallback } from 'react'
import {
  useSharedBrowserDispatchContext,
  useSharedBrowserStateContext,
} from '../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useR2StateContext } from '../../../providers/r2Provider/R2Context'
import { getImagesFromApidList } from '../../../../shared/utils/getImagesFromApidList'

export default function useSetViewMode() {
  const { viewMode, focusedIndex, thumbnails, unifiedApidList } = useSharedBrowserStateContext()
  const { childGroups } = useR2StateContext()
  const sharedBrowserDispatch = useSharedBrowserDispatchContext()

  // Calling this function assumes that the group is a split group, thus the ability
  // to switch between single and group view modes (single is viewing a child of a split group)
  const setViewMode = useCallback(
    (newViewMode) => {
      if (newViewMode === viewMode) return

      let newIndex = null
      let newThumbnails = null
      if (newViewMode === 'single') {
        const currentApid = unifiedApidList[focusedIndex] // Where we are now before the state change

        // Helper functions
        const match = (apid) => apid === currentApid
        const getIndexFrom = (childGroup) => childGroup.childIds.findIndex(match)
        const hasCurrentImage = (group) => group.childIds.some(match)

        const groupWithCurrentImage = childGroups.find(hasCurrentImage)
        newIndex = getIndexFrom(groupWithCurrentImage) // In reference to the new child group images being viewed
        newThumbnails = getImagesFromApidList(groupWithCurrentImage.childIds) // Becomes the smaller child group images list

        if (newIndex === -1) throw new Error('Image not found in child groups')
      } else if (newViewMode === 'group') {
        const currentApid = thumbnails[focusedIndex].id // Where we are now before the state change

        newIndex = unifiedApidList.findIndex((apid) => apid === currentApid) // In reference to the larger set of images
        newThumbnails = getImagesFromApidList(unifiedApidList) // Becomes the larger set of images
      }

      sharedBrowserDispatch({
        type: 'BATCH',
        value: [
          { type: 'SET_THUMBNAILS', value: newThumbnails },
          { type: 'SET_VIEW_MODE', value: newViewMode },
          { type: 'SET_FOCUSED_INDEX', value: newIndex },
        ],
      })
    },
    [unifiedApidList, thumbnails, viewMode, focusedIndex, sharedBrowserDispatch, childGroups]
  )

  return setViewMode
}
